import { useEffect } from 'react'
import useStore from '../store/useStore'
import { useSiteDetail, useDamageHistory } from '../hooks/useQueries'
import NarrativeStream from './NarrativeStream'
import { useOnboarding } from '../hooks/useOnboarding'
import { track } from '../umami'

function escHtml(str: string | undefined | null): string {
  if (!str) return ''
  const el = document.createElement('span')
  el.textContent = str
  return el.innerHTML
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function SkeletonLoader() {
  return (
    <>
      <div className="skeleton-block skeleton-title" />
      <div className="skeleton-row">
        <div className="skeleton-block skeleton-tag" />
        <div className="skeleton-block skeleton-tag" />
        <div className="skeleton-block skeleton-tag" />
      </div>
      <div className="skeleton-block skeleton-image" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line skeleton-line-short" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line skeleton-line-short" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line skeleton-line-medium" />
    </>
  )
}

function DamageHistory({ siteId }: { siteId: string }) {
  const { data: reports } = useDamageHistory(siteId)

  if (!reports?.length) return null

  return (
    <div id="damage-history">
      <h3>Damage History</h3>
      <div className="damage-timeline">
        {reports.map((r) => (
          <div key={r.id} className="damage-event">
            <div className="damage-event-header">
              <span className={`severity-badge severity-${r.severity}`}>{r.severity}</span>
              <span className="damage-date">{formatDate(r.report_date)}</span>
            </div>
            {r.event_title && <h4>{escHtml(r.event_title)}</h4>}
            {r.notes && <p>{escHtml(r.notes)}</p>}
            {(r.before_image_url || r.after_image_url) && (
              <div className="damage-images">
                {r.before_image_url && (
                  <div className="damage-img">
                    <img src={r.before_image_url} alt="Before" />
                    <span>Before</span>
                  </div>
                )}
                {r.after_image_url && (
                  <div className="damage-img">
                    <img src={r.after_image_url} alt="After" />
                    <span>After</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function DetailPanel() {
  const selectedSiteId = useStore((s) => s.selectedSiteId)
  const closeSite = useStore((s) => s.closeSite)
  const { data: site, isLoading } = useSiteDetail(selectedSiteId)
  const { dismissOnboarding } = useOnboarding()

  const isOpen = selectedSiteId !== null

  // ESC key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSite()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [closeSite])

  // Track site opened — and treat it as onboarding-complete
  useEffect(() => {
    if (site && !site.error && selectedSiteId) {
      track('site_opened', {
        site_id: selectedSiteId,
        site_name: site.name,
        country: site.country,
      })
      dismissOnboarding()
    }
  }, [site, selectedSiteId, dismissOnboarding])

  const handleClose = () => {
    closeSite()
    track('detail_panel_closed')
  }

  return (
    <aside id="detail-panel" className={isOpen ? '' : 'hidden'}>
      <div className="drawer-handle" aria-hidden="true" />
      <button id="detail-close" aria-label="Close site details panel" onClick={handleClose}>
        &times;
      </button>
      <div id="detail-content">
        {!isOpen ? null : isLoading || !site ? (
          <SkeletonLoader />
        ) : site.error ? (
          <p>Site not found.</p>
        ) : (
          <>
            <h2>{escHtml(site.name)}</h2>
            <div className="meta">
              {site.country && <span>{escHtml(site.country)}</span>}
              {site.period && <span>{escHtml(site.period)}</span>}
              {site.site_type && <span>{escHtml(site.site_type)}</span>}
              {site.damage_status && site.damage_status !== 'intact' && (
                <span className={`damage-badge damage-${site.damage_status}`}>
                  {escHtml(site.damage_status)}
                </span>
              )}
            </div>
            {site.image_url && <img className="site-image" src={site.image_url} alt={site.name} />}
            <div className="narrative" id="narrative-content">
              <NarrativeStream siteId={selectedSiteId!} siteName={site.name} />
            </div>
            <DamageHistory siteId={selectedSiteId!} />
            <div className="links">
              {site.wikipedia_url && (
                <a href={site.wikipedia_url} target="_blank" rel="noopener noreferrer">
                  Wikipedia
                </a>
              )}
              {site.osm?.tags?.website && (
                <a href={site.osm.tags.website} target="_blank" rel="noopener noreferrer">
                  Official site
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  )
}
